import React, { useEffect, useMemo, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { toast } from "react-toastify";

import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import { useTheme } from "@material-ui/core/styles";
import VisibilityIcon from "@material-ui/icons/Visibility";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { isRequestCanceled } from "../../utils/requestCancel";
import ContactTagTicketsModal from "../../components/Dashboard/ContactTagTicketsModal";
import { getChartTextColor, getChartGridColor } from "./chartTheme";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const DEFAULT_BAR_COLOR = "#CC0036";
const UNTAGGED_COLOR = "#9E9E9E";
// Nomes de tag são longos e uma empresa pode ter dezenas delas: o gráfico mostra
// as maiores e a tabela abaixo lista todas.
const MAX_CHART_TAGS = 15;
const BAR_HEIGHT = 32;

const buildOptions = (textColor, gridColor) => ({
  indexAxis: "y",
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: {
      beginAtZero: true,
      // Espaço à direita para o número que fica depois da barra.
      grace: "10%",
      ticks: { color: textColor, precision: 0 },
      grid: { color: gridColor }
    },
    y: {
      ticks: { color: textColor },
      grid: { display: false }
    }
  },
  plugins: {
    legend: { display: false },
    title: {
      display: true,
      text: i18n.t("dashboard.ticketsByContactTag.chartTitle"),
      color: textColor
    },
    datalabels: {
      display: true,
      anchor: "end",
      align: "end",
      color: textColor,
      font: { weight: "bold" },
      formatter: value => {
        const total = Math.trunc(Number(value) || 0);
        return total > 0 ? total : "";
      }
    }
  }
});

const formatPercent = (value, total) => {
  if (!total) return "—";
  return `${((value / total) * 100).toFixed(1)}%`;
};

const TagDot = ({ color }) => (
  <span
    style={{
      display: "inline-block",
      width: 10,
      height: 10,
      borderRadius: "50%",
      marginRight: 8,
      backgroundColor: color || DEFAULT_BAR_COLOR
    }}
  />
);

// Chamados abertos no período agrupados pelas tags do contato (ContactTags).
// Contagens para quem acessa o Dashboard; a lista de chamados ("Ver") só para admin.
export const ChartsTicketsByContactTag = ({ initialDate, finalDate, isAdmin, paperClass }) => {
  const theme = useTheme();
  const isDark = theme.palette.type === "dark";
  const textColor = getChartTextColor(isDark);
  const gridColor = getChartGridColor(isDark);

  const [report, setReport] = useState({ total: 0, untagged: 0, byTag: [] });
  const [selectedTag, setSelectedTag] = useState(null);

  useEffect(() => {
    if (!initialDate || !finalDate) return;

    const controller = new AbortController();
    (async () => {
      try {
        const { data } = await api.get("/dashboard/ticketsByContactTag", {
          params: { initialDate, finalDate },
          signal: controller.signal
        });
        setReport({
          total: Number(data?.total) || 0,
          untagged: Number(data?.untagged) || 0,
          byTag: Array.isArray(data?.byTag) ? data.byTag : []
        });
      } catch (error) {
        if (isRequestCanceled(error)) return;
        toast.error(i18n.t("dashboard.ticketsByContactTag.loadError"));
      }
    })();

    return () => controller.abort();
  }, [initialDate, finalDate]);

  const chartTags = useMemo(() => report.byTag.slice(0, MAX_CHART_TAGS), [report.byTag]);

  const dataCharts = useMemo(
    () => ({
      labels: chartTags.map(row => row.tagName),
      datasets: [
        {
          label: i18n.t("dashboard.ticketsByContactTag.quantity"),
          data: chartTags.map(row => Number(row.total) || 0),
          backgroundColor: chartTags.map(row => row.tagColor || DEFAULT_BAR_COLOR)
        }
      ]
    }),
    [chartTags]
  );

  const chartHeight = Math.max(120, chartTags.length * BAR_HEIGHT + 60);

  const renderViewButton = row =>
    isAdmin ? (
      <Button
        size="small"
        color="primary"
        variant="outlined"
        startIcon={<VisibilityIcon />}
        onClick={() => setSelectedTag(row)}
      >
        {i18n.t("dashboard.ticketsByContactTag.view")}
      </Button>
    ) : null;

  // Nenhum chamado do período tem contato com tag (todos "Sem tag" ou período vazio):
  // não exibe o gráfico.
  if (report.byTag.length === 0) {
    return null;
  }

  return (
    <Grid item xs={12}>
      <Paper className={paperClass}>
        <Typography component="h2" variant="h6" color="primary" gutterBottom>
          {i18n.t("dashboard.ticketsByContactTag.title")}
        </Typography>

        <Typography variant="body2" color="textSecondary">
          {i18n.t("dashboard.ticketsByContactTag.subtitle", {
            total: report.total,
            untagged: report.untagged
          })}
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {i18n.t("dashboard.ticketsByContactTag.multiTagNote")}
          {report.byTag.length > MAX_CHART_TAGS &&
            ` ${i18n.t("dashboard.ticketsByContactTag.topNote", { count: MAX_CHART_TAGS })}`}
        </Typography>

        <div style={{ position: "relative", height: chartHeight, maxWidth: "100%" }}>
          <Bar options={buildOptions(textColor, gridColor)} data={dataCharts} />
        </div>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t("dashboard.ticketsByContactTag.tag")}</TableCell>
              <TableCell align="right">
                {i18n.t("dashboard.ticketsByContactTag.quantity")}
              </TableCell>
              <TableCell align="right">
                {i18n.t("dashboard.ticketsByContactTag.percent")}
              </TableCell>
              {isAdmin && <TableCell align="right" />}
            </TableRow>
          </TableHead>
          <TableBody>
            {report.byTag.map(row => (
              <TableRow key={row.tagId} hover>
                <TableCell>
                  <TagDot color={row.tagColor} />
                  {row.tagName}
                </TableCell>
                <TableCell align="right">{row.total}</TableCell>
                <TableCell align="right">{formatPercent(row.total, report.total)}</TableCell>
                {isAdmin && <TableCell align="right">{renderViewButton(row)}</TableCell>}
              </TableRow>
            ))}
            {report.untagged > 0 && (
              <TableRow hover>
                <TableCell>
                  <TagDot color={UNTAGGED_COLOR} />
                  {i18n.t("dashboard.ticketsByContactTag.untagged")}
                </TableCell>
                <TableCell align="right">{report.untagged}</TableCell>
                <TableCell align="right">
                  {formatPercent(report.untagged, report.total)}
                </TableCell>
                {isAdmin && (
                  <TableCell align="right">
                    {renderViewButton({ untagged: true })}
                  </TableCell>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>

        {isAdmin && (
          <ContactTagTicketsModal
            open={!!selectedTag}
            onClose={() => setSelectedTag(null)}
            tag={selectedTag}
            initialDate={initialDate}
            finalDate={finalDate}
          />
        )}
      </Paper>
    </Grid>
  );
};

export default ChartsTicketsByContactTag;
